/**
 * EmailEinstellungenPage — E-Mail-Konten verwalten (IMAP/SMTP)
 * Konten hinzufügen, testen, abrufen, bearbeiten, löschen
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Mail, Plus, Trash2, RefreshCw, CheckCircle, XCircle,
  Settings, Eye, EyeOff, Loader2, Server, Shield,
  ToggleLeft, ToggleRight, Inbox, Send, Zap, FolderSync,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';

const API = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

// ── Hilfsfunktionen ──
async function api(path, opts = {}) {
  const token = localStorage.getItem('token');
  const res = await fetch(`${API}/api/email${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...opts.headers },
  });
  return res.json();
}

const EMPTY_FORM = {
  name: '', email_address: '', username: '', password: '',
  imap_server: '', imap_port: 993, imap_use_ssl: true,
  smtp_server: '', smtp_port: 587, smtp_use_tls: true,
  auto_fetch: true, auto_analyse: true, auto_aufgaben: true, auto_entwurf: true,
};

// ── Konto-Karte ──
function AccountCard({ account, onRefresh }) {
  const [testing, setTesting] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const handleTest = async () => {
    setTesting(true); setTestResult(null);
    const r = await api(`/accounts/${account.id}/test`, { method: 'POST' });
    setTestResult(r);
    setTesting(false);
    if (r.success) toast.success('Verbindung OK');
    else toast.error(r.error || 'Fehler');
  };

  const handleFetch = async () => {
    setFetching(true);
    const r = await api(`/accounts/${account.id}/fetch`, { method: 'POST' });
    setFetching(false);
    if (r.success) toast.success(`${r.new_emails || 0} neue E-Mail(s) abgerufen`);
    else toast.error(r.error || 'Fehler beim Abruf');
    onRefresh();
  };

  const handleDelete = async () => {
    if (!window.confirm(`E-Mail-Konto "${account.name}" wirklich löschen?`)) return;
    setDeleting(true);
    await api(`/accounts/${account.id}`, { method: 'DELETE' });
    toast.success('Konto gelöscht');
    onRefresh();
  };

  const handleToggle = async (field, value) => {
    await api(`/accounts/${account.id}`, {
      method: 'PUT',
      body: JSON.stringify({ [field]: value }),
    });
    onRefresh();
  };

  return (
    <Card data-testid={`email-account-${account.id}`}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <Mail className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <CardTitle className="text-base">{account.name}</CardTitle>
              <p className="text-xs text-slate-500">{account.email_address}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant={account.aktiv ? 'default' : 'secondary'} className="text-xs">
              {account.aktiv ? 'Aktiv' : 'Inaktiv'}
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Server-Info */}
        <div className="grid grid-cols-2 gap-3 text-xs">
          <div className="flex items-center gap-1.5 text-slate-500">
            <Inbox className="w-3.5 h-3.5" />
            <span>{account.imap_server}:{account.imap_port}</span>
            {account.imap_use_ssl && <Shield className="w-3 h-3 text-green-500" />}
          </div>
          <div className="flex items-center gap-1.5 text-slate-500">
            <Send className="w-3.5 h-3.5" />
            <span>{account.smtp_server}:{account.smtp_port}</span>
            {account.smtp_use_tls && <Shield className="w-3 h-3 text-green-500" />}
          </div>
        </div>

        {/* Letzter Abruf */}
        {account.last_fetch && (
          <p className="text-xs text-slate-400">
            Letzter Abruf: {new Date(account.last_fetch).toLocaleString('de-DE')} — {account.last_fetch_count || 0} E-Mail(s)
          </p>
        )}

        {/* Auto-Toggles */}
        <div className="grid grid-cols-2 gap-2">
          {[
            { field: 'auto_fetch', label: 'Auto-Abruf', icon: FolderSync },
            { field: 'auto_analyse', label: 'KI-Analyse', icon: Zap },
            { field: 'auto_aufgaben', label: 'Aufgaben erstellen', icon: CheckCircle },
            { field: 'aktiv', label: 'Konto aktiv', icon: ToggleRight },
          ].map(({ field, label, icon: Icon }) => (
            <div key={field} className="flex items-center justify-between py-1">
              <div className="flex items-center gap-1.5 text-xs text-slate-600">
                <Icon className="w-3.5 h-3.5" />
                {label}
              </div>
              <Switch
                checked={account[field] ?? false}
                onCheckedChange={(v) => handleToggle(field, v)}
                data-testid={`toggle-${field}-${account.id}`}
              />
            </div>
          ))}
        </div>

        {/* Test-Ergebnis */}
        {testResult && (
          <div className={`p-2.5 rounded-lg text-xs ${testResult.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
            {testResult.success ? (
              <div className="flex items-center gap-1.5">
                <CheckCircle className="w-4 h-4" />
                {testResult.message}
              </div>
            ) : (
              <div className="flex items-center gap-1.5">
                <XCircle className="w-4 h-4" />
                {testResult.error}
              </div>
            )}
          </div>
        )}

        {/* Aktions-Buttons */}
        <div className="flex gap-2 pt-1">
          <Button variant="outline" size="sm" onClick={handleTest} disabled={testing} data-testid={`test-btn-${account.id}`}>
            {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Server className="w-3.5 h-3.5 mr-1" />}
            Testen
          </Button>
          <Button variant="outline" size="sm" onClick={handleFetch} disabled={fetching} data-testid={`fetch-btn-${account.id}`}>
            {fetching ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
            Jetzt abrufen
          </Button>
          <Button variant="ghost" size="sm" className="ml-auto text-red-500 hover:text-red-700 hover:bg-red-50"
            onClick={handleDelete} disabled={deleting} data-testid={`delete-btn-${account.id}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Neues Konto Formular ──
function AddAccountForm({ onSaved, onCancel }) {
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSave = async () => {
    if (!form.name || !form.email_address || !form.imap_server || !form.smtp_server || !form.username || !form.password) {
      toast.error('Bitte alle Pflichtfelder ausfüllen');
      return;
    }
    setSaving(true);
    const r = await api('/accounts', { method: 'POST', body: JSON.stringify(form) });
    setSaving(false);
    if (r.success) {
      toast.success('E-Mail-Konto angelegt');
      onSaved();
    } else {
      toast.error(r.detail || 'Fehler beim Anlegen');
    }
  };

  return (
    <Card className="border-2 border-dashed border-blue-200 bg-blue-50/30" data-testid="add-account-form">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Plus className="w-5 h-5 text-blue-600" />
          Neues E-Mail-Konto
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Basis */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Kontoname *</Label>
            <Input placeholder="z.B. Büro-Mail" value={form.name} onChange={e => set('name', e.target.value)}
              data-testid="input-name" />
          </div>
          <div>
            <Label className="text-xs">E-Mail-Adresse *</Label>
            <Input placeholder="name@domain.de" value={form.email_address} onChange={e => set('email_address', e.target.value)}
              data-testid="input-email" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Benutzername *</Label>
            <Input placeholder="name@domain.de" value={form.username} onChange={e => set('username', e.target.value)}
              data-testid="input-username" />
          </div>
          <div>
            <Label className="text-xs">Passwort *</Label>
            <div className="relative">
              <Input type={showPw ? 'text' : 'password'} value={form.password}
                onChange={e => set('password', e.target.value)} data-testid="input-password" />
              <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                onClick={() => setShowPw(!showPw)}>
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>

        {/* IMAP */}
        <div>
          <p className="text-xs font-medium text-slate-500 flex items-center gap-1 mb-2">
            <Inbox className="w-3.5 h-3.5" /> Posteingang (IMAP)
          </p>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <Input placeholder="imap.domain.de" value={form.imap_server} onChange={e => set('imap_server', e.target.value)}
                data-testid="input-imap-server" />
            </div>
            <div>
              <Input type="number" value={form.imap_port} onChange={e => set('imap_port', parseInt(e.target.value) || 993)}
                data-testid="input-imap-port" />
            </div>
          </div>
          <div className="flex items-center gap-2 mt-2">
            <Switch checked={form.imap_use_ssl} onCheckedChange={v => set('imap_use_ssl', v)} />
            <span className="text-xs text-slate-500">SSL verwenden</span>
          </div>
        </div>

        {/* SMTP */}
        <div>
          <p className="text-xs font-medium text-slate-500 flex items-center gap-1 mb-2">
            <Send className="w-3.5 h-3.5" /> Postausgang (SMTP)
          </p>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <Input placeholder="smtp.domain.de" value={form.smtp_server} onChange={e => set('smtp_server', e.target.value)}
                data-testid="input-smtp-server" />
            </div>
            <div>
              <Input type="number" value={form.smtp_port} onChange={e => set('smtp_port', parseInt(e.target.value) || 587)}
                data-testid="input-smtp-port" />
            </div>
          </div>
          <div className="flex items-center gap-2 mt-2">
            <Switch checked={form.smtp_use_tls} onCheckedChange={v => set('smtp_use_tls', v)} />
            <span className="text-xs text-slate-500">TLS verwenden</span>
          </div>
        </div>

        {/* Auto-Optionen */}
        <div className="grid grid-cols-2 gap-x-4 gap-y-2 pt-1">
          {[
            { k: 'auto_fetch', l: 'Automatischer Abruf (alle 30 Min)' },
            { k: 'auto_analyse', l: 'KI-Analyse neuer E-Mails' },
            { k: 'auto_aufgaben', l: 'Aufgaben automatisch erstellen' },
            { k: 'auto_entwurf', l: 'Antwortentwürfe generieren' },
          ].map(({ k, l }) => (
            <div key={k} className="flex items-center gap-2">
              <Switch checked={form[k]} onCheckedChange={v => set(k, v)} />
              <span className="text-xs text-slate-600">{l}</span>
            </div>
          ))}
        </div>

        <div className="flex gap-2 pt-2">
          <Button onClick={handleSave} disabled={saving} data-testid="save-account-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
            Konto anlegen
          </Button>
          <Button variant="outline" onClick={onCancel}>Abbrechen</Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Hauptseite ──
export default function EmailEinstellungenPage() {
  const [accounts, setAccounts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [fetchingAll, setFetchingAll] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [accs, st] = await Promise.all([api('/accounts'), api('/stats')]);
    setAccounts(Array.isArray(accs) ? accs : []);
    setStats(st);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleFetchAll = async () => {
    setFetchingAll(true);
    const r = await api('/fetch-now', { method: 'POST' });
    setFetchingAll(false);
    if (r.success) {
      toast.success(`${r.total_new || 0} neue E-Mail(s) abgerufen`);
    } else {
      toast.error(r.error || r.message || 'Fehler');
    }
    load();
  };

  return (
    <div className="space-y-6" data-testid="email-einstellungen-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Mail className="w-6 h-6 text-blue-600" />
            E-Mail-Einstellungen
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            IMAP-Konten verwalten, automatischer Abruf alle 30 Minuten
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleFetchAll} disabled={fetchingAll || accounts.length === 0}
            data-testid="fetch-all-btn">
            {fetchingAll ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}
            Alle abrufen
          </Button>
          <Button onClick={() => setShowForm(true)} disabled={showForm} data-testid="add-account-btn">
            <Plus className="w-4 h-4 mr-1" /> Konto hinzufügen
          </Button>
        </div>
      </div>

      {/* Statistiken */}
      {stats && (
        <div className="grid grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-4 pb-3 text-center">
              <p className="text-2xl font-bold text-blue-600">{stats.accounts_aktiv || 0}</p>
              <p className="text-xs text-slate-500">Aktive Konten</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3 text-center">
              <p className="text-2xl font-bold text-slate-700">{stats.total_emails || 0}</p>
              <p className="text-xs text-slate-500">E-Mails gesamt</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3 text-center">
              <p className="text-2xl font-bold text-amber-600">{stats.unread_emails || 0}</p>
              <p className="text-xs text-slate-500">Ungelesen</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3 text-center">
              <p className="text-2xl font-bold text-green-600">{stats.zugeordnet || 0}</p>
              <p className="text-xs text-slate-500">Klient zugeordnet</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3 text-center">
              <p className="text-2xl font-bold text-green-600">{stats.aufgaben_erstellt || 0}</p>
              <p className="text-xs text-slate-500">Aufgaben erstellt</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Formular */}
      {showForm && (
        <AddAccountForm
          onSaved={() => { setShowForm(false); load(); }}
          onCancel={() => setShowForm(false)}
        />
      )}

      {/* Konten-Liste */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400 mr-2" />
          <span className="text-slate-500">Laden...</span>
        </div>
      ) : accounts.length === 0 && !showForm ? (
        <Card className="border-dashed" data-testid="no-accounts-placeholder">
          <CardContent className="py-12 text-center">
            <Mail className="w-12 h-12 mx-auto text-slate-200 mb-3" />
            <h3 className="text-lg font-medium text-slate-600 mb-1">Keine E-Mail-Konten konfiguriert</h3>
            <p className="text-sm text-slate-400 mb-4">
              Konfiguriere ein IMAP-Konto, um E-Mails automatisch abzurufen und von der KI analysieren zu lassen.
            </p>
            <Button onClick={() => setShowForm(true)} data-testid="add-first-account-btn">
              <Plus className="w-4 h-4 mr-1" /> Erstes Konto einrichten
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {accounts.map(acc => (
            <AccountCard key={acc.id} account={acc} onRefresh={load} />
          ))}
        </div>
      )}

      {/* Info-Box */}
      <Card className="bg-slate-50/50">
        <CardContent className="py-4">
          <h4 className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-1.5">
            <Settings className="w-4 h-4" /> Funktionsweise
          </h4>
          <ul className="text-xs text-slate-500 space-y-1">
            <li>Neue E-Mails werden alle 30 Minuten automatisch per IMAP abgerufen</li>
            <li>Automatische Klient-Zuordnung: Aktenzeichen im Betreff, Absender aus dem Netzwerk, Name im Text</li>
            <li>Claude analysiert jede E-Mail und erkennt Handlungsbedarf (Fristen, Antworten, Aufgaben)</li>
            <li>Aufgaben, Anhänge und E-Mails werden automatisch dem erkannten Klienten zugeordnet</li>
            <li>Alle E-Mails erscheinen im Posteingang mit Klient-Zuordnung</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
